// components/legal/PrivacyPolicy.js
import React, { useEffect } from 'react';
import styled from 'styled-components';
import Container from '../common/Container';
import { SectionTitle } from '../common/SharedStyles';
import Button from '../common/Button';
import { Link } from 'react-router-dom';

const LegalSection = styled.section`
  padding: 6rem 0;
  background-color: var(--background-primary);
`;

const LegalContainer = styled(Container)`
  max-width: 800px;
`;

const EffectiveDate = styled.p`
  font-style: italic;
  color: var(--text-secondary);
  margin-bottom: 2rem;
  text-align: center;
`;

const SectionHeading = styled.h2`
  color: var(--accent-secondary);
  margin-top: 2.5rem;
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const CompanyInfo = styled.div`
  background-color: var(--background-secondary);
  padding: 1.5rem;
  border-radius: 5px;
  margin: 1.5rem 0;
  border: 1px solid var(--border-color);
`;

const BackButton = styled(Button)`
  margin-top: 3rem;
`;

const PrivacyPolicy = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <LegalSection id="privacy-policy">
      <LegalContainer>
        <SectionTitle>Privacy Policy</SectionTitle>
        <EffectiveDate>Effective Date: August 26th, 2026</EffectiveDate>

        <p>
          This Privacy Policy describes how Signage LLC ("Signage," "we," "our," or "us") collects, uses,
          and protects your information when you use our website and our software application Sivo. By
          accessing or using our services, you agree to the terms outlined below.
        </p>

        <SectionHeading>1. Information We Collect</SectionHeading>
        <p>We may collect the following information when you interact with our services:</p>
        <ul>
          <li>Personal Information: Names, business names, and email addresses.</li>
          <li>Payment Information: Collected and processed securely through Stripe.</li>
          <li>Usage Data: Information about how you interact with our software.</li>
          <li>Business Configuration Data: We may collect information that users voluntarily enter into the platform, including custom suppliers, supplier information, custom materials, material specifications, pricing information, and other business-related configuration data used within the Software.</li>
          <li>Cookies: Our website uses cookies to improve user experience and functionality.</li>
        </ul>

        <SectionHeading>2. How We Use Your Information</SectionHeading>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide, maintain, and improve our software and services.</li>
          <li>Process payments and manage subscriptions.</li>
          <li>Communicate with you regarding your account, support, and important updates.</li>
          <li>Send necessary service-related emails or information as needed.</li>
          <li>Product Improvement &amp; Database Development: We may analyze supplier names, material names, product categories, and other business configuration information entered into the platform to improve the Software, expand our supplier and material databases, develop new features, and enhance the overall user experience. We do not sell user data to third parties.</li>
        </ul>

        <SectionHeading>3. Third-Party Services</SectionHeading>
        <ul>
          <li>Payments: All payment transactions are processed securely through Stripe.</li>
          <li>Hosting: We use GoDaddy for website hosting.</li>
        </ul>
        <p>
          We do not sell or share your information with outside advertisers or third-party analytics
          platforms.
        </p>

        <SectionHeading>4. Data Storage & Security</SectionHeading>
        <p>
          Your information is stored securely on servers located in the United States. We use
          industry-standard security measures to protect your data against unauthorized access, loss,
          misuse, or disclosure.
        </p>

        <SectionHeading>5. Your Rights</SectionHeading>
        <p>You have the right to:</p>
        <ul>
          <li>Request deletion of your account and associated data.</li>
          <li>Opt out of email communications by contacting us directly.</li>
        </ul>
        <p>All requests can be submitted to support@signage-llc.com.</p>

        <SectionHeading>6. Children's Privacy</SectionHeading>
        <p>
          Our services are intended only for individuals aged 18 and older. We do not knowingly collect
          personal information from anyone under 18.
        </p>

        <SectionHeading>7. Changes to This Privacy Policy</SectionHeading>
        <p>
          We may update this Privacy Policy occasionally to reflect changes in our practices. Any updates
          will be posted on this page with a revised effective date.
        </p>

        <SectionHeading>8. Contact Us</SectionHeading>
        <CompanyInfo>
          <p>
            If you have any questions about this Privacy Policy or how your information is handled, you may
            contact us at:
          </p>
          <p>
            Signage LLC<br />
            216 Remount Dr<br />
            San Antonio, TX 78218<br />
            Email: support@signage-llc.com
          </p>
        </CompanyInfo>

        <Link to="/">
          <BackButton variant="primary">Back to Website</BackButton>
        </Link>
      </LegalContainer>
    </LegalSection>
  );
};

export default PrivacyPolicy;
